import BasePaymentController from 'app/src/controllers/payments/BasePaymentController';
import Decimal from 'app/src/data/base/Decimal';
import DepositManager from 'app/src/payments/DepositManager';
import SaleManager from 'app/src/payments/SaleManager';
import PaymentTotalView from 'app/src/views/payments/PaymentTotalView';
import Modules from 'shared_modules/Modules';

export default class PaymentTotalViewController extends BasePaymentController {
  constructor(saleManager) {
    super(saleManager);

    this.view = new PaymentTotalView({
      el: 'div.register-payment-detail', // attach to the existing element
      model: this._saleManager.sale,
      viewData: {
        saleData: this._saleManager.toJSON(),
        isTipEnabled: this._saleManager.isTipEnabled,
        isSurchargeEnabled: this._saleManager.isSurchargeEnabled,
        depositTitle: this._getDepositTitle(false),
      },
    });
  }

  /**
   * Initializes and displays the payment total view.  Binds to sale manager events.
   */
  show() {
    super.show();

    this.listenTo(this._saleManager, SaleManager.Events.PAYMENT_TOTAL_CHANGED, this.onTotalChanged);
    this.listenTo(this._saleManager, SaleManager.Events.BALANCE_CHANGED, this.onBalanceChanged);
    this.listenTo(this._saleManager, SaleManager.Events.CALCTIPS_CHANGED, this.onCalcTipsChanged);
    this.listenTo(
      this._saleManager,
      SaleManager.Events.CALCSURCHARGES_CHANGED,
      this.onCalcSurchargesChanged
    );
    this.listenTo(
      this._saleManager,
      SaleManager.Events.COMPLETE_SALE_FAILED,
      this.setInitialViewState
    );
    this.listenTo(this._saleManager, SaleManager.Events.SALE_REFRESHED, this.setInitialViewState);

    // refresh the view when deposits have been added or removed
    this.listenTo(
      this._saleManager.deposits,
      DepositManager.Events.DEPOSITS_CHANGED,
      this.onDepositsChanged
    );
  }

  /**
   * Sets the initial state of dynamic data within the view.
   */
  setInitialViewState() {
    const calcTips = this._saleManager.sale.get('calcTips') || 0;
    const calcSurcharges = Modules.hasModule('payments.surcharge')
      ? this._saleManager.sale.get('calcSurcharges') || 0
      : 0;

    this._setViewPaymentTotal();

    this._setViewBalance(this._saleManager.pendingBalance);

    this._setCashRoundingInfo();

    this.view.setDepositAmount(this._saleManager.deposits.depositTotal);
    this.view.enableRemoveDeposits(true);
    this.view.showDeposits(!this._saleManager.deposits.depositTotal.isZero());
    this.view.setDepositRefundAmount(this._saleManager.deposits.refundTotal);
    this.view.showDepositRefunds(!this._saleManager.deposits.refundTotal.isZero());

    const depositTotal = this._saleManager.deposits.depositTotal;
    if (Modules.hasModule('customers.invoicing_integration.free_tier')) {
      this.view.updateSaleTotal(this._saleManager.saleTotal.plus(depositTotal));
    } else {
      this.view.updateSaleTotal(this.view.viewData.saleData.saleTotal.plus(depositTotal));
    }

    this.view.updateTipAmount(calcTips);
    if (Modules.hasModule('payments.surcharge')) {
      this.view.updateSurchargeAmount(calcSurcharges);
    }
  }

  /**
   * Event handler for when the sale manager total has changed
   */
  onTotalChanged() {
    this._setViewPaymentTotal();
  }

  /**
   * Event handler for when the sale manager balance has changed
   *
   * @param balance {Decimal} The new sale balance
   */
  onBalanceChanged(balance) {
    this._setViewBalance(balance);
    this._setCashRoundingInfo();
  }

  _setCashRoundingInfo() {
    if (
      Modules.hasModule('register.sales.cash_rounding') &&
      this._saleManager.isCashRoundingActive
    ) {
      const hasCashPayment = !this._saleManager.cashPayment.isZero();
      this.view.showCashRoundingInfo(hasCashPayment);
      this.view.setCashRoundingDelta(this._saleManager.cashRoundingDelta);
      this.view.setCashRoundedBalance(this._saleManager.cashRoundedBalance);
    }
  }

  /**
   * Event handler for when the sale manager calcTips has changed
   *
   * @param calcTips {Decimal} The new calcTips
   */
  onCalcTipsChanged(calcTips) {
    this.view.updateTipAmount(calcTips);
  }

  /**
   * Event handler for when the sale manager calcSurcharges has changed
   *
   * @param calcSurcharges {Decimal} The new calcSurcharges
   */
  onCalcSurchargesChanged(calcSurcharges) {
    this.view.updateSurchargeAmount(calcSurcharges);
  }

  /**
   * Event handler for when deposits in the sale manager are removed
   */
  onRemoveDepositsClicked() {
    this.view.enableRemoveDeposits(false);
    this._saleManager.removeDeposits();
  }

  /**
   * Event handler for when a deposit is made
   *
   * @param {Boolean} isAccountPayment true if the deposit was an account payment
   */
  onDepositsChanged(isAccountPayment) {
    this.setInitialViewState();
    this.view.setDepositTitle(this._getDepositTitle(isAccountPayment));
  }

  _getDepositTitle(isAccountPayment) {
    if (Modules.hasModule('register.payment.deposit_by_paylink')) return 'Account Deposit';
    return isAccountPayment ? 'Account Payment' : 'Deposit';
  }

  /**
   * Updates the view's balance
   *
   * @param balance {Decimal} The sale balance
   * @private
   */
  _setViewBalance(balance) {
    let displayAmount = balance;

    if (!this._saleManager.isRefundSale()) {
      // the existing view likes to show 0 balance when change is due;
      // let's keep it the same for
      displayAmount = Decimal.max(0, balance);
    }

    if (
      Modules.hasModule('register.sales.cash_rounding') &&
      this._saleManager.isCashRoundingActive
    ) {
      if (this._saleManager.isBalanceSatisfiedWithCashRounding) {
        displayAmount = new Decimal(0);
      }
    }

    this.view.updateBalance(displayAmount);
  }

  /**
   * Sets the payment total within the view.
   *
   * @private
   */
  _setViewPaymentTotal() {
    this.view.updateTotal(this._saleManager.pendingPaymentsTotal);
  }
}
